import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { ASPECTS, CANVAS, type Aspect } from '../../api/imagePresets';

/** 이 페이지의 모달 껍데기. Esc·바깥 클릭으로 닫힌다. */
export const Modal: React.FC<{
  title: string;
  onClose: () => void;
  wide?: boolean;
  children: React.ReactNode;
}> = ({ title, onClose, wide, children }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div
        className={`relative bg-white rounded-xl shadow-xl w-full ${wide ? 'max-w-5xl' : 'max-w-2xl'} max-h-[90vh] flex flex-col`}
      >
        <div className="border-b border-slate-200 px-5 py-3 flex items-center justify-between shrink-0">
          <h3 className="text-base font-bold text-slate-800">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-full text-slate-500"
            aria-label="close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="overflow-y-auto p-5">{children}</div>
      </div>
    </div>
  );
};

/** 비율 모양을 그대로 그린 작은 사각형. source 는 점선(입력 사진 비율을 따른다). */
export const AspectShape: React.FC<{ aspect: Aspect; size?: number; active?: boolean }> = ({
  aspect,
  size = 22,
  active,
}) => {
  const tone = active ? 'border-blue-600 bg-blue-100' : 'border-slate-400 bg-white';
  if (aspect === 'source') {
    return (
      <span
        className={`inline-block border border-dashed ${active ? 'border-blue-600' : 'border-slate-400'}`}
        style={{ width: size * 0.8, height: size * 0.8 }}
      />
    );
  }
  const [w, h] = CANVAS[aspect];
  const scale = size / Math.max(w, h);
  return <span className={`inline-block border ${tone}`} style={{ width: w * scale, height: h * scale }} />;
};

export const AspectPicker: React.FC<{
  value: Aspect;
  onChange: (aspect: Aspect) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled }) => {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" role="radiogroup">
      {ASPECTS.map((aspect) => {
        const active = aspect === value;
        return (
          <button
            key={aspect}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={disabled}
            onClick={() => onChange(aspect)}
            className={`flex flex-col items-center justify-end gap-1.5 rounded-lg border px-2 pt-3 pb-2 h-24 transition-colors disabled:cursor-not-allowed ${
              active ? 'border-blue-600 bg-blue-50/60' : 'border-slate-200 hover:bg-slate-50'
            }`}
          >
            <span className="flex-1 flex items-center">
              <AspectShape aspect={aspect} size={34} active={active} />
            </span>
            <span className={`text-sm font-semibold ${active ? 'text-blue-700' : 'text-slate-700'}`}>
              {aspect === 'source' ? t('imagePresets.aspectSource') : aspect}
            </span>
            <span className="text-[11px] text-slate-400 tabular-nums">
              {aspect === 'source' ? t('imagePresets.aspectSourceHint') : CANVAS[aspect].join('×')}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export const Toggle: React.FC<{
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  label: string;
}> = ({ checked, onChange, disabled, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={(e) => {
      e.stopPropagation();
      onChange(!checked);
    }}
    className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 ${
      checked ? 'bg-emerald-500' : 'bg-slate-300'
    }`}
  >
    <span
      className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
        checked ? 'translate-x-4' : 'translate-x-0.5'
      }`}
    />
  </button>
);

export const FieldMessage: React.FC<{ messages?: string[] }> = ({ messages }) =>
  messages?.length ? (
    <p className="mt-1 text-xs text-red-600" role="alert">
      {messages.join(' · ')}
    </p>
  ) : null;
